import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';

type CloudWatchSdk = typeof import('@aws-sdk/client-cloudwatch-logs');

const NO_STACK = 'No stack trace available';

interface CloudWatchHandle {
  sdk: CloudWatchSdk;
  client: import('@aws-sdk/client-cloudwatch-logs').CloudWatchLogsClient;
}

@Injectable({ providedIn: 'root' })
export class AWSLoggerService {
  readonly logGroupName: string = environment.logger.enabled
    ? environment.logger.logGroupName
    : 'AppFrontendTest';
  readonly logStreamName = `session-${Date.now()}`;

  private logStreamCreated = false;
  // The AWS SDK is loaded on the first logged error so it stays out of the
  // initial bundle; the promise is cached so concurrent errors share one load.
  private handlePromise: Promise<CloudWatchHandle | null> | null = null;

  private loadClient(): Promise<CloudWatchHandle | null> {
    this.handlePromise ??= (async () => {
      try {
        const [sdk, { fromCognitoIdentityPool }] = await Promise.all([
          import('@aws-sdk/client-cloudwatch-logs'),
          import('@aws-sdk/credential-provider-cognito-identity'),
        ]);
        const client = new sdk.CloudWatchLogsClient({
          region: environment.logger.region,
          credentials: fromCognitoIdentityPool({
            clientConfig: { region: environment.logger.region },
            identityPoolId: environment.logger.identityPoolId,
          }),
        });
        return { sdk, client };
      } catch (error) {
        console.error('Error loading CloudWatch logger:', error);
        return null;
      }
    })();
    return this.handlePromise;
  }

  private async createLogStream({ sdk, client }: CloudWatchHandle) {
    try {
      await client.send(
        new sdk.CreateLogStreamCommand({
          logGroupName: this.logGroupName,
          logStreamName: this.logStreamName,
        }),
      );
      this.logStreamCreated = true;
    } catch (error) {
      console.error('Error creating log stream:', error);
    }
  }

  async logError(error: unknown, context?: Record<string, unknown>) {
    if (!environment.logger.enabled) {
      return;
    }

    const timestamp = Date.now();
    const errorMessage = {
      type: 'error',
      pageUrl: window.location.href,
      userAgent: window.navigator.userAgent,
      timestamp,
      ...describeError(error),
      ...(context ?? {}),
    };

    const handle = await this.loadClient();
    if (!handle) {
      return;
    }

    if (!this.logStreamCreated) {
      await this.createLogStream(handle);
    }

    try {
      await handle.client.send(
        new handle.sdk.PutLogEventsCommand({
          logGroupName: this.logGroupName,
          logStreamName: this.logStreamName,
          logEvents: [{ message: serialize(errorMessage), timestamp }],
        }),
      );
    } catch (err) {
      console.error('Error sending log to CloudWatch:', err);
    }
  }
}

/**
 * Flattens an unknown error into a structured, log-friendly record. Unlike the
 * previous implementation it never discards context: HTTP errors keep their
 * status/url/response body, real Errors keep their stack, and structured
 * payloads (e.g. from the HTTP logging interceptor) keep every field.
 */
function describeError(error: unknown): Record<string, unknown> {
  if (error instanceof HttpErrorResponse) {
    return describeHttpError(error);
  }

  if (error instanceof Error) {
    return describeErrorInstance(error);
  }

  if (error && typeof error === 'object') {
    return {
      kind: 'object',
      message: 'Unknown error',
      stack: NO_STACK,
      ...(error as Record<string, unknown>),
    };
  }

  return {
    kind: typeof error,
    message: String(error ?? 'Unknown error'),
    stack: NO_STACK,
  };
}

function describeHttpError(error: HttpErrorResponse): Record<string, unknown> {
  return {
    kind: 'http',
    message: error.message || 'Unknown HTTP error',
    httpStatus: error.status,
    httpUrl: error.url ?? undefined,
    response: safeSerialize(error.error),
    stack: NO_STACK,
  };
}

function describeErrorInstance(error: Error): Record<string, unknown> {
  return {
    kind: error.name || 'Error',
    message: error.message || 'Unknown error',
    stack: error.stack || NO_STACK,
  };
}

/** Strips non-serialisable values so a single bad field can't lose the whole log. */
function safeSerialize(value: unknown): unknown {
  if (value == null || typeof value !== 'object') {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return String(value);
  }
}

/** JSON.stringify with a fallback so a serialisation failure still emits a record. */
function serialize(payload: Record<string, unknown>): string {
  try {
    return JSON.stringify(payload);
  } catch {
    return JSON.stringify({
      type: 'error',
      timestamp: payload['timestamp'],
      message: 'Failed to serialise error payload',
    });
  }
}
